import { create } from 'zustand';
import type { UserIdentity } from '@/api/models';

/**
 * The access token lives in memory and nowhere else.
 *
 * The refresh token is an HttpOnly cookie the browser holds and JavaScript
 * cannot read, so a script that reaches this store gets a token measured in
 * minutes rather than a refresh family. That is also why the markdown renderer
 * is strict: a prompt-injected repository reaching XSS in this tab would be
 * reaching exactly this object.
 *
 * Nothing here is persisted. A reload re-establishes the session from the
 * cookie via POST /auth/refresh.
 */
interface AuthState {
  accessToken: string | null;
  user: UserIdentity | null;
  /** null until the first refresh attempt resolves — distinguishes "checking" from "signed out". */
  bootstrapped: boolean;
  setSession: (token: string, user: UserIdentity) => void;
  setUser: (user: UserIdentity) => void;
  clear: () => void;
  markBootstrapped: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: null,
  user: null,
  bootstrapped: false,
  setSession: (accessToken, user) => set({ accessToken, user, bootstrapped: true }),
  setUser: (user) => set({ user }),
  clear: () => set({ accessToken: null, user: null, bootstrapped: true }),
  markBootstrapped: () => set({ bootstrapped: true }),
}));

export const getAccessToken = (): string | null => useAuthStore.getState().accessToken;
